import React, { useEffect } from 'react'
import { EditorCanvas } from './editor/components/EditorCanvas.js'
import { InteractiveCanvas } from './editor/components/InteractiveCanvas.js'
import { EditorNavBar } from './editor/components/EditorNavBar.js'
import { CollapsiblePanel } from './editor/components/CollapsiblePanel.js'
import { RightSidebar } from './editor/components/RightSidebar.js'
import { useEditorStore } from './editor/store.js'

export default function App() {
  const markup = useEditorStore(state => state.markup)
  const setMarkup = useEditorStore(state => state.setMarkup)
  const buildTreeFromMarkup = useEditorStore(state => state.buildTreeFromMarkup)
  const parseErrors = useEditorStore(state => state.parseErrors)
  const viewMode = useEditorStore(state => state.viewMode)
  const isHtmlEditorExpanded = useEditorStore(state => state.isHtmlEditorExpanded)
  const toggleHtmlEditor = useEditorStore(state => state.toggleHtmlEditor)
  const undo = useEditorStore(state => state.undo)
  const redo = useEditorStore(state => state.redo)
  const canUndo = useEditorStore(state => state.canUndo)
  const canRedo = useEditorStore(state => state.canRedo)
  const projects = useEditorStore(state => state.projects)
  const currentProject = useEditorStore(state => state.currentProject)
  const createProject = useEditorStore(state => state.createProject)

  // Create initial project if none exists
  useEffect(() => {
    if (projects.length === 0 && !currentProject) {
      createProject('Untitled')
    }
  }, [projects.length, currentProject, createProject])

  useEffect(() => {
    const handle = window.setTimeout(() => {
      buildTreeFromMarkup(markup)
    }, 200)

    return () => window.clearTimeout(handle)
  }, [markup, buildTreeFromMarkup])

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        if (canUndo()) {
          e.preventDefault()
          undo()
        }
      } else if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'z') {
        if (canRedo()) {
          e.preventDefault()
          redo()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [undo, redo, canUndo, canRedo])

  return (
    <div className="app-container">
      <EditorNavBar />

      <div className="app-main">
        <div className="app-workspace">
          {parseErrors.length > 0 && (
            <div className="status status--error">
              <strong>Parse error</strong>
              <ul>
                {parseErrors.map((error, index) => (
                  <li key={`${error}-${index}`}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="canvas-container">
            <div className="canvas-content">
              <div className="canvas-area">
                {viewMode === 'interactive' ? <InteractiveCanvas /> : <EditorCanvas />}
              </div>
              <RightSidebar />
            </div>
          </div>

          <CollapsiblePanel isExpanded={isHtmlEditorExpanded} onToggle={toggleHtmlEditor} title="HTML Markup">
            <textarea
              className="html-editor-textarea"
              value={markup}
              onChange={event => setMarkup(event.target.value)}
              spellCheck={false}
            />
          </CollapsiblePanel>
        </div>
      </div>
    </div>
  )
}
